import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import styles from './CurtainCourierSelectModal.module.css';
import { getAvailableCurtainCouriers } from '../route/routeApi';

/**
 * Модальное окно для выбора шторного курьера
 * @param {boolean} show - Показать/скрыть модальное окно
 * @param {number} helperCourierId - ID курьера-помощника
 * @param {string} date - Дата смены
 * @param {Function} onClose - Функция закрытия модального окна
 * @param {Function} onSelect - Функция выбора курьера (mainCourierId)
 */
const CurtainCourierSelectModal = ({ show, helperCourierId, date, onClose, onSelect }) => {
    const [couriers, setCouriers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show && helperCourierId && date) {
            loadAvailableCouriers();
        }
    }, [show, helperCourierId, date]);

    const loadAvailableCouriers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getAvailableCurtainCouriers({
                helperCourierId: helperCourierId,
                date: date
            });
            if (response.data && response.data.success !== false) {
                // Фильтруем курьеров: показываем только тех, у кого есть маршрут
                // (на случай, если бэкенд вернет курьеров без маршрутов)
                // Если поле hasRoute отсутствует, считаем что курьер есть в списке (бэкенд уже отфильтровал)
                const couriersWithRoutes = (response.data.couriers || []).filter(courier => 
                    courier.hasRoute !== false
                );
                setCouriers(couriersWithRoutes);
            } else {
                setError(response.data?.error || 'Не удалось загрузить список курьеров');
            }
        } catch (err) {
            console.error('Ошибка при загрузке списка курьеров:', err);
            setError(err.response?.data?.error || 'Ошибка при загрузке списка курьеров');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = (mainCourierId) => {
        onSelect(mainCourierId);
        onClose();
    };

    if (!show) return null;

    const modalContent = (
        <>
            <div className={styles.Backdrop} onClick={onClose} />
            <div className={styles.Modal} role="dialog" aria-modal="true">
                <div className={styles.ModalDialog}>
                    <div className={styles.ModalContent}>
                        <div className={styles.ModalHeader}>
                            <div className={styles.ModalTitle}>
                                Выберите шторного курьера
                            </div>
                            <button 
                                type="button"
                                onClick={onClose}
                                className={styles.CloseButton}
                                aria-label="Закрыть"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className={styles.ModalBody}>
                            {isLoading && (
                                <div className={styles.Loading}>Загрузка...</div>
                            )}
                            {error && (
                                <div className={styles.Error}>{error}</div>
                            )}
                            {!isLoading && !error && couriers.length === 0 && (
                                <div className={styles.Empty}>
                                    Нет доступных шторных курьеров с маршрутами в этой смене
                                </div>
                            )}
                            {!isLoading && !error && couriers.length > 0 && (
                                <ul className={styles.CourierList}>
                                    {couriers.map((courier) => (
                                        <li 
                                            key={courier.id} 
                                            className={styles.CourierItem}
                                            onClick={() => handleSelect(courier.id)}
                                        >
                                            <div className={styles.CourierName}>{courier.name}</div>
                                            {courier.phone && (
                                                <div className={styles.CourierPhone}>{courier.phone}</div>
                                            )}
                                            {courier.workTime && (
                                                <div className={styles.CourierTime}>
                                                    {courier.workTime.time_from} - {courier.workTime.time_to}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className={styles.ModalFooter}>
                            <button type="button" className={styles.CancelButton} onClick={onClose}>
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    return createPortal(modalContent, document.body);
};

export default CurtainCourierSelectModal;

